import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { professionalCost, professionalRunning, setProfessionalPreference, useProfessionalAvailability, type ProfessionalStatus } from '../lib/professional.js';

export function ProfessionalControl({ meetingId, userId, connected, onActiveChange, listenOnly = false, readOnly = false }: { readOnly?: boolean; meetingId: string; userId: string; connected: boolean; listenOnly?: boolean; onActiveChange?: (active: boolean) => void }) {
  const availability = useProfessionalAvailability(userId);
  const accountDenied = availability?.eligible === false;
  const [statusResult, setStatusResult] = useState<{ userId: string; meetingId: string; value: ProfessionalStatus } | null>(null);
  const status = statusResult && statusResult.userId === userId && statusResult.meetingId === meetingId ? statusResult.value : null;
  const setStatus = (value: ProfessionalStatus | null) => setStatusResult(value ? { userId, meetingId, value } : null);
  const [busy, setBusy] = useState(false);
  const [extending, setExtending] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now);
  const ownedSession = useRef<{ meetingId: string; sessionId: string } | null>(null);
  const revokedCleanup = useRef<{ sessionId: string; pending: boolean; acknowledged: boolean } | null>(null);
  const lifecycle = useRef(0);
  const actionRevision = useRef(0);
  const changing = useRef(false);
  useEffect(() => { onActiveChange?.(connected && !accountDenied && status?.eligible === true && status.state === 'active'); }, [connected, accountDenied, status?.eligible, status?.state, onActiveChange]);
  useEffect(() => {
    lifecycle.current++;
    actionRevision.current++; changing.current = false;
    let alive = true;
    setStatus(null); setBusy(false); setExtending(false); setError('');
    // Meeting status can prove eligibility and restore Stop while an account
    // availability check retries. A definitive account denial still fences it.
    if (!connected || accountDenied) return;
    const refresh = async () => {
      const revision = actionRevision.current;
      try {
        const next = await api.professional.get(meetingId);
        if (alive && !changing.current && revision === actionRevision.current) {
          setStatus(next); setError('');
          if (!next.eligible) {
            // A redacted read proves no closure. Retain our exact attempt for
            // scoped cancellation now and for departure cleanup if its receipt is lost.
            const owned = ownedSession.current;
            const previous = revokedCleanup.current;
            if (owned?.meetingId === meetingId && (previous?.sessionId !== owned.sessionId || (!previous.pending && !previous.acknowledged))) {
              const cleanup = { sessionId: owned.sessionId, pending: true, acknowledged: false };
              revokedCleanup.current = cleanup;
              void api.professional.stop(owned.meetingId, owned.sessionId)
                // A concealed 200 acknowledges cancellation, not provider closure.
                // Deduplicate polling only; keep ownedSession for a scoped departure retry.
                .then(() => { cleanup.acknowledged = true; })
                .catch(() => {})
                .finally(() => { cleanup.pending = false; });
            }
            return;
          }
          if (!professionalRunning(next) || (next.session && next.session.id !== ownedSession.current?.sessionId)) ownedSession.current = null;
        }
      }
      catch { if (alive && !changing.current && revision === actionRevision.current) setError('Professional is unavailable on this server. Normal voice is still available.'); }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 2000);
    return () => {
      lifecycle.current++;
      alive = false; window.clearInterval(timer);
      const owned = ownedSession.current;
      if (owned?.meetingId === meetingId) {
        const cleanup = api.professional.stop(meetingId, owned.sessionId);
        if (ownedSession.current === owned) ownedSession.current = null;
        void cleanup.catch(() => {});
      }
    };
  }, [meetingId, connected, userId, accountDenied]);

  const running = professionalRunning(status);
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  const change = async () => {
    if (accountDenied || !status?.eligible || !status.canManage || (!running && (listenOnly || !status.configured || status.voiceMicrosPerSecond === null))) return;
    const generation = lifecycle.current;
    const starting = !running;
    // Assign the id before POST so cleanup can target an uncertain Start without
    // closing a replacement session that was opened after reconnect.
    const sessionId = starting ? crypto.randomUUID() : ownedSession.current?.sessionId ?? status?.session?.id;
    if (starting) ownedSession.current = { meetingId, sessionId: sessionId! };
    changing.current = true; actionRevision.current++;
    setBusy(true); setError('');
    try {
      const next = running ? await api.professional.stop(meetingId, sessionId) : await api.professional.start(meetingId, 1800, sessionId);
      // A successful POST can outlive this control. Its session still needs an
      // explicit Stop, even though cleanup ran before the POST returned.
      if (generation !== lifecycle.current) {
        if (starting) await api.professional.stop(meetingId, sessionId);
        return;
      }
      if (next.eligible && (!professionalRunning(next) || (next.session && next.session.id !== sessionId))) ownedSession.current = null;
      // A person's call switch also syncs their Settings opt-in. Departure does
      // not change that preference; every new paid session still needs an explicit Start click.
      if (starting) {
        if (next.eligible && (next.state === 'connecting' || next.state === 'active') && next.session?.id === sessionId) setProfessionalPreference(userId, true);
      } else if (next.eligible && !professionalRunning(next)) setProfessionalPreference(userId, false);
      setStatus(next);
    } catch (failure) {
      if (starting) {
        // A lost POST response can still leave a paid session running. Keep
        // cleanup ownership until an explicit Stop or a later read confirms closure.
        try {
          const closed = await api.professional.stop(meetingId, sessionId);
          if (generation === lifecycle.current) {
            if (closed.eligible && (!professionalRunning(closed) || (closed.session && closed.session.id !== sessionId))) ownedSession.current = null;
            setStatus(closed);
          }
        } catch {
          if (generation === lifecycle.current) setStatusResult(current => current && current.userId === userId && current.meetingId === meetingId ? { ...current, value: { ...current.value, state: 'finalization_unknown' } } : current);
        }
      }
      if (generation === lifecycle.current) setError(failure instanceof Error ? failure.message : 'Professional could not change state.');
    } finally {
      if (generation === lifecycle.current) {
        changing.current = false; actionRevision.current++; setBusy(false);
      }
    }
  };
  const extend = async () => {
    if (!status?.eligible || !status.canManage || status.state !== 'active' || !status.session || extending) return;
    const generation = lifecycle.current;
    const revision = ++actionRevision.current;
    const target = status.session.id;
    setExtending(true); setError('');
    try {
      const next = await api.professional.extend(meetingId, target, status.session.maxSeconds);
      if (generation === lifecycle.current && revision === actionRevision.current && !changing.current && next.session?.id === target) setStatus(next);
    } catch (failure) {
      if (generation === lifecycle.current && revision === actionRevision.current) setError(failure instanceof Error ? failure.message : 'Could not add time.');
    } finally {
      if (generation === lifecycle.current) {
        if (revision === actionRevision.current) actionRevision.current++;
        setExtending(false);
      }
    }
  };
  const knownEligible = availability?.eligible === true || status?.eligible === true;
  if (!connected || accountDenied || !knownEligible || status?.eligible === false) return null;
  const configured = !listenOnly && status?.configured === true && status.voiceMicrosPerSecond !== null && status.maxSeconds >= 1800;
  const perMinute = status?.voiceMicrosPerSecond == null ? null : professionalCost(status.voiceMicrosPerSecond * 60);
  const elapsed = status?.session ? Math.max(0, Math.floor((now - Date.parse(status.session.createdAt)) / 1000)) : 0;
  const remaining = status?.session ? Math.max(0, status.session.maxSeconds - elapsed) : 0;
  const endingSoon = status?.state === 'active' && remaining <= 300;
  return <section className={`room-professional${running ? ' on' : ''}`} aria-label="Professional voice controls">
    <div className="room-professional-heading">
      <div><strong><span className="room-professional-label">Professional voice </span>{status?.state === 'active' ? 'ON' : running ? status?.state.replaceAll('_', ' ').toUpperCase() : 'OFF'}</strong>
        {perMinute && <span className="room-professional-rate" role={running ? 'status' : undefined}>{running && <>{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')} · </>}{perMinute}/min</span>}
      </div>
      {!readOnly && <button type="button" role="switch" aria-label="Professional voice" aria-checked={running}
        className="btn btn-primary" title={running ? "Stop Professional" : "Start Professional"}
        disabled={!status || busy || !status.canManage || (!running && !configured)} onClick={() => void change()}>
        {busy ? 'Updating…' : running ? 'Stop' : 'Start'}
      </button>}
    </div>
    {readOnly && running && status?.session && <p className="room-professional-left" role="status">{Math.ceil(remaining / 60)} minutes left in this session. Stop it from the call bar.</p>}
    {readOnly && !running && <p className="muted small">Start or stop Professional from the call bar.</p>}
    {endingSoon && !readOnly && <div className="room-time-notice" role="alert">
      <span>{Math.ceil(remaining / 60)} minutes left</span>
      {status.canManage && <button className="btn btn-secondary" disabled={extending} onClick={() => void extend()}>{extending ? 'Adding…' : 'Add 10 minutes'}</button>}
    </div>}
    {!readOnly && !running && status && !configured && <p>{listenOnly ? 'Silent · transcript only' : 'Not available yet. Normal voice remains available.'}</p>}
    {status && !status.canManage && <p className="muted small">The person who started this call or a project owner controls Professional voice.</p>}
    {error && <p role="alert">{error}</p>}
    {status?.state === 'finalization_unknown' && <p role="alert">Final usage pending. Stop again to retry closure.</p>}
    {status?.session?.finalMicros != null && <p className="room-professional-receipt" role="status">Professional stopped · {status.session.usageSeconds ?? 'Unknown'} seconds · {professionalCost(status.session.finalMicros)}</p>}
  </section>;
}
